import React, { useCallback, useEffect, useState } from 'react';
import { Edit } from '@material-ui/icons';
import Box from '@material-ui/core/Box';
import TableContainer from '@material-ui/core/TableContainer';
import { useSnackbar } from 'notistack';
import Table from '@material-ui/core/Table';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import { Dialog, FormControlLabel } from '@material-ui/core';
import { Field, Form, Formik } from 'formik';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import LinearProgress from '@material-ui/core/LinearProgress';
import DialogActions from '@material-ui/core/DialogActions';
import makeStyles from '@material-ui/core/styles/makeStyles';
import FormikTextField from 'components/atoms/FormikInput/FormikInput';
import TablePagination from 'components/atoms/TablePagination/TablePagination';
import Spin from 'components/atoms/Spin/Spin';
import { useDictionaryApi } from 'api/dictionary';
import SearchBar from 'components/atoms/SearchBar/SearchBar';
import { Order } from 'utils/sort';
import TableSortCell from 'components/atoms/TablePagination/TableSortCell';
import { EmcTypeDto, initEmcType } from '../../api/emc';
import { FormikSwitch } from '../../components/atoms/FormikSwitch/FormikSwitch';

const useStyles = makeStyles((theme) => ({
    root: {
        maxWidth: 380,
        width: '100%',
    },
    mt5: {
        marginTop: theme.spacing(3),
    },
}));

const EmcTypes = () => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    // Table and stuff
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<EmcTypeDto[]>([]);
    const [item, setItem] = useState<EmcTypeDto>(initEmcType());

    // Pagination
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(50);
    const [total, setTotal] = useState(0);
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof EmcTypeDto>('id');
    const [search, setSearch] = React.useState<string | null>(null);
    const { getPaged } = useDictionaryApi<EmcTypeDto>('/mail-type/list');
    const { post, put } = useDictionaryApi<EmcTypeDto>('/mail-type');

    // Fetch
    const fetch = useCallback(async () => {
        setLoading(true);
        try {
            const x = await getPaged(page, perPage, `${orderBy},${order}`, search);
            setItems(x.content);
            setTotal(x.total_elements);
        } catch {
            enqueueSnackbar('Ошибка при получении данных', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [getPaged, page, perPage, orderBy, order, search, enqueueSnackbar]);

    const [regionOpen, setRegionOpen] = useState(false);

    const handleAdd = () => {
        setItem(initEmcType());
        setRegionOpen(true);
    };

    const handleEdit = (item: EmcTypeDto) => {
        setItem(item);
        setRegionOpen(true);
    };

    const handleRequestSort = (property: keyof EmcTypeDto) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    useEffect(() => {
        fetch();
    }, [fetch]);

    return (
        <Box position={'relative'}>
            <SearchBar
                buttonText="Добавить"
                placeholder="Введите текст"
                onButtonClick={handleAdd}
                onSearchChange={(event: any) => {
                    setSearch(event.target.value);
                }}
            ></SearchBar>
            <Box position={'relative'}>
                {loading && <Spin />}
                <TableContainer>
                    <Table size={'small'} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableSortCell
                                    label={'№'}
                                    orderBy={orderBy}
                                    order={order}
                                    property={'id'}
                                    onRequestSort={handleRequestSort}
                                />
                                <TableCell>Код</TableCell>
                                <TableCell>Наименование</TableCell>
                                <TableCell>Отправка внештатным курьерам</TableCell>
                                <TableCell>Действия</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item, index) => (
                                <TableRow key={index} hover={true}>
                                    <TableCell>{page * perPage + index + 1}</TableCell>
                                    <TableCell>{item.code}</TableCell>
                                    <TableCell>{item.description}</TableCell>
                                    <TableCell>
                                        {item.is_send_courier ? 'Включен' : 'Выключен'}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => handleEdit(item)}>
                                            <Edit />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            <TablePagination
                total={total}
                perPage={perPage}
                page={page}
                onChangePage={setPage}
                onChangeRowsPerPage={setPerPage}
            />
            <Dialog open={regionOpen} fullWidth={true} onClose={() => setRegionOpen(false)}>
                <Formik
                    enableReinitialize={true}
                    initialValues={item}
                    onSubmit={(values, { setSubmitting }) => {
                        setSubmitting(true);

                        if (values.id) {
                            put(values)
                                .then(() => {
                                    setRegionOpen(false);
                                    fetch();
                                })
                                .catch(() =>
                                    enqueueSnackbar('Ошибка при сохранении данных', {
                                        variant: 'error',
                                    })
                                )
                                .finally(() => setSubmitting(false));
                        } else {
                            post(values)
                                .then(() => {
                                    setRegionOpen(false);
                                    fetch();
                                })
                                .catch(() =>
                                    enqueueSnackbar('Ошибка при сохранении данных', {
                                        variant: 'error',
                                    })
                                )
                                .finally(() => setSubmitting(false));
                        }
                    }}
                >
                    {({ isSubmitting }) => (
                        <Form className={classes.root}>
                            <DialogTitle>Редактирвание действии</DialogTitle>
                            <DialogContent>
                                <Box display="flex" flexDirection="column">
                                    <Field
                                        component={FormikTextField}
                                        className={classes.mt5}
                                        name="code"
                                        label="Код"
                                        disabled={item.id}
                                    />
                                    <Field
                                        component={FormikTextField}
                                        className={classes.mt5}
                                        name="description"
                                        label="Наименование"
                                    />
                                    <FormControlLabel
                                        className={classes.mt5}
                                        control={
                                            <Field
                                                component={FormikSwitch}
                                                name="is_send_courier"
                                                type="checkbox"
                                                color="primary"
                                            />
                                        }
                                        label="Отправка внештатным курьерам"
                                    />
                                </Box>

                                {isSubmitting && <LinearProgress />}
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setRegionOpen(false)}>Отмена</Button>
                                <Button type={'submit'}>Ок</Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </Box>
    );
};

export default EmcTypes;
